import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Controller } from 'react-hook-form'
import { FormControl, InputLabel, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { selectClubs, clearClubs } from 'store/clubs/slice'
import { searchClubs } from 'store/clubs/thunks'
import { debounce } from 'utils/debounce'

interface Props {
  name: string
  label?: string
  control: any
  initialValue: SelectOption | null
  initialClubName?: string
  countryId: number
}

export default function ClubSelect({
  name,
  label,
  control,
  initialValue,
  initialClubName,
  countryId,
}: Props): JSX.Element {
  const intl = useIntl()
  const [value, setValue] = useState<SelectOption | null>(initialValue)
  const { clubs, status } = useSelector(selectClubs)
  const dispatch = useDispatch()

  useEffect(() => {
    if (!countryId) {
      setValue(null)
      dispatch(clearClubs())
    }
  }, [countryId, dispatch])

  useEffect(() => {
    if (countryId && initialClubName && !initialValue) {
      dispatch(searchClubs({ data: { countryIds: [countryId], clubName: initialClubName, activeCourses: true } }))
    }
  }, [countryId, initialClubName, initialValue, dispatch])

  useEffect(() => {
    if (initialValue && !value) {
      setValue(initialValue)
    }
  }, [initialValue, value])

  const handleChange = (newValue: SelectOption | null) => {
    setValue(newValue)
    return newValue
  }

  const clubsOptions: SelectOption[] = clubs.map((club: Club) => {
    return { key: club.id, label: club.name }
  })

  const searchForClubs = debounce((searchString: string | undefined) => {
    if (searchString && searchString.length >= 3) {
      dispatch(
        searchClubs({
          data: { countryIds: [countryId], clubName: searchString, activeClubs: true, activeCourses: true },
        }),
      )
    }
  }, 500)

  return (
    <>
      <FormControl margin="normal" fullWidth>
        <FieldLabel>{label ? label : intl.formatMessage({ id: 'strings.homeClub' })}</FieldLabel>
        <Controller
          control={control}
          name={name}
          defaultValue={value}
          render={({ field: { onChange } }) => (
            <Autocomplete
              loading={status === 'loading'}
              disableListWrap
              onChange={(event, newValue) => onChange(handleChange(newValue))}
              onInputChange={(event: any) => searchForClubs(event?.target?.value)}
              options={clubsOptions}
              getOptionLabel={(option) => option.label || ''}
              autoHighlight
              value={value}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder={intl.formatMessage({ id: 'strings.search' })} />
              )}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              noOptionsText={intl.formatMessage({ id: 'strings.typeMin3Characters' })}
              filterOptions={(option) => option}
            />
          )}
        />
      </FormControl>
    </>
  )
}

const FieldLabel = styled(InputLabel)`
  padding: 0 10px;
  margin-top: -20px;
  transition: none;
  transform: none;
`
